<template>
    <div>
        <rkm-modal
            persistent
            ref="RkmModal"
            title="Escolha de horario"
        >
            <rkm-form
                v-if="model"
                ref="RkmForm"
                show-legend
            >
                <rkm-row v-if="data.form.horariosComSituacao && data.form.horariosComSituacao.length > 0">
                    <rkm-col>
                        <rkm-alert color="warning">
                            <ul class="pl-3 mb-0">
                                <li
                                    v-for="horarioComSituacao in data.form.horariosComSituacao"
                                    :key="horarioComSituacao.inicio"
                                >
                                    Das {{horarioComSituacao.inicio}} às {{horarioComSituacao.fim}}: {{horarioComSituacao.situacaoNome}}
                                </li>
                            </ul>
                        </rkm-alert>
                    </rkm-col>
                </rkm-row>

                <rkm-row v-if="data.form.quantidadeDemandaEspontanea !== null && data.form.quantidadeDemandaEspontanea > 0">
                    <rkm-col>
                        <rkm-alert color="info">
                            Quantidade de demandas espontâneas para a data: {{data.form.quantidadeDemandaEspontanea}}
                        </rkm-alert>
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-radio-group
                            :options="horarios"
                            :validations="validations.horario"
                            @input="handleHorarioInput"
                            label="Horario"
                            v-model="model.horario"
                            tooltip-bottom="O total de pacientes corresponde à soma dos pacientes agendados previamente com as demandas espontâneas registradas na mesma jornada."
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-textarea
                            :validations="validations.observacao"
                            label="Observação"
                            rows="4"
                            v-model="model.observacao"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row v-if="horarioMaximoAtingido">
                    <rkm-col>
                        <rkm-form-textarea
                            :validations="validations.motivoAgendamentoExcedente"
                            label="Motivo"
                            rows="4"
                            v-model="model.motivoAgendamentoExcedente"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <template v-slot:footer="{cancel}">
                <rkm-button @click="cancel">
                    Fechar
                </rkm-button>

                <rkm-button
                    @click="store"
                    authorize="store-ambulatorio-agendamento-retroativo"
                    :disabled="isSaving"
                    color="primary"
                >
                    Salvar
                </rkm-button>
            </template>
        </rkm-modal>

        <ModalSupervisor
            @authorized="authorized"
            @canceled="canceled"
            ref="ModalSupervisor"
        />
    </div>
</template>

<script>
// Components.
import ModalSupervisor from '@/components/ambulatorio/components/supervisor/Modal'

// Mixins.
import {error, file, object, repository, stringCase} from '@/mixins'

// Vuex.
import {mapActions,mapGetters} from 'vuex'

export default {
    components: {ModalSupervisor},
    computed: {
        horarios() {
            return this.data.form.horarios && this.data.form.horarios.map(horario => {
                return {
                    value: horario.horario,
                    text: `${horario.horario} - ${horario.quantidadeAgendada}/${horario.quantidadeTotal}`
                }
            })
        },
        validations() {
            return {
                horario: {required: true},
                observacao: {max: 500},
                motivoAgendamentoExcedente: {
                    required: true,
                    max: 500,
                },
            }
        },
    },
    created() {
        this.repositories = {
            agendamentoRetroativo: this.getConectaSusRepository('ambulatorio.agendamentoRetroativo'),
            impressao: this.getConectaSusRepository('impressao.ambulatorio'),
        }
    },
    data: () => ({
        horarioMaximoAtingido: false,
        model: null,
        tipoId: null,
        infraestruturaId: null,
        isSaving: false,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        ...mapGetters(['getLotacao']),
        open(infraestruturaId, ocupacaoId, especialidadeId, usuarioId, tipoId, data) {
            this.resetForm()

            this.tipoId = tipoId
            this.infraestruturaId = infraestruturaId

            this.$refs.RkmModal.show()

            const request = {
                origem: 'cadastro',
                listar: 'horarios',
                pacienteId: this.pacienteId,
                infraestruturaId,
                ocupacaoId,
                especialidadeId,
                usuarioId,
                tipoId,
                data,
            }

            this.setLoading()

            this.repositories.agendamentoRetroativo.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(({data}) => {
                    this.data.form.horarios = Object.assign(data.horarios)
                    this.data.form.horariosComSituacao = Object.assign(data.horariosComSituacao)
                    this.data.form.quantidadeDemandaEspontanea = data.quantidadeDemandaEspontanea

                    this.model = this.clone(this.data.model)
                    this.model.demandaEspontaneaObservaVagaAtendimento = data.model?.demandaEspontaneaObservaVagaAtendimento
                        ?? this.model.demandaEspontaneaObservaVagaAtendimento

                    delete this.model.horario
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleHorarioInput(value) {
            this.horarioMaximoAtingido = false
            this.model.motivoAgendamentoExcedente = null

            const horario = this.data.form.horarios.find(horario => horario.horario === value)
            const quantidadeDemandaEspontanea = this.data.form.quantidadeDemandaEspontanea ?? 0
            const observaVagaAtendimento = this.model.demandaEspontaneaObservaVagaAtendimento

            const horarioMaximoAtingido = this.data.form.horarios
                .filter(horario => {
                    if (observaVagaAtendimento) {
                        return (horario.quantidadeAgendada + quantidadeDemandaEspontanea) >= horario.quantidadeTotal
                    }

                    return horario.quantidadeAgendada >= horario.quantidadeTotal
                })
                .find(horario => horario.horario == value)

            if (horarioMaximoAtingido || (horario && horario.obrigatorioAutenticacaoSupervisor)) {
                this.$refs.ModalSupervisor.open({
                    isHorarioMaximoAtingido: !!horarioMaximoAtingido,
                    infraestruturaId: this.infraestruturaId,
                    lotacaoInfraestruturaId: this.getLotacao().infraestruturaId,
                })
            }
        },
        canceled() {
            this.model.horario = null

            this.$nextTick().then(() => this.$refs.RkmForm.resetValidator())
        },
        store() {

            if (this.isSaving) return;
            this.isSaving = true;

            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (!valid) {
                        this.isSaving = false;
                        return;
                    }

                    const horario = this.data.form.horarios.find(horario => horario.horario === this.model.horario)

                    let request = {
                        pacienteId: this.pacienteId,
                        tipoId: this.tipoId,
                        jornadaId: horario.jornadaId,
                        horario: horario.horario,
                        observacao: this.model.observacao,
                    }

                    if (this.horarioMaximoAtingido) {
                        request.motivoAgendamentoExcedente = this.model.motivoAgendamentoExcedente
                    }

                    this.setLoading()

                    this.repositories.agendamentoRetroativo.store(this.camelObjectKeysToSnakeRecursive(request))
                        .then(response => {
                            this.print(response.data.id)
                            this.$refs.RkmModal.hide()
                            this.$emit('saved', response.data)
                            this.showSaveNotification()
                        })
                        .catch(this.handleError)
                        .finally(() => {
                            this.unsetLoading();
                            this.isSaving = false;
                        });
                })
        },
        print(id) {
            this.setLoading()

            this.repositories.impressao.agendamento(id)
                .then(({data, headers}) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        resetForm() {
            this.data.form.horarios = null
            this.data.form.horariosComSituacao = null
            this.data.form.quantidadeDemandaEspontanea = null
            this.horarioMaximoAtingido = false
            this.model = null
            this.tipoId = null
        },
        authorized(value) {
            this.horarioMaximoAtingido = value
        },
    },
    mixins: [error, file, object, repository, stringCase],
    props: {
        data: {type: Object, required: true},
        pacienteId: {type: [Number, String], required: true},
    },
}
</script>